import { useQuery } from "@tanstack/react-query";
import { fetchGeoRun } from "@/lib/api";
import PromptResponsesBlock from "./PromptResponsesBlock";

export default function MencionesPanel({ clientId, runId }) {
  const q = useQuery({
    queryKey: ["geo-run", clientId, runId],
    queryFn: () => fetchGeoRun(clientId, runId),
    enabled: !!runId,
  });

  if (!runId) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 p-10 text-center bg-slate-50/50" data-testid="geo-menciones-empty-no-run">
        <p className="text-sm text-slate-600">
          Todavía no hay un análisis ejecutado para este cliente.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Hacé click en <strong>Ejecutar análisis</strong> arriba para correr el primer barrido. La primera run se marca como <strong>Snapshot Día 0</strong> automáticamente.
        </p>
      </div>
    );
  }
  if (q.isLoading) {
    return <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500">Cargando resultados…</div>;
  }

  const results = (q.data?.results || []).filter((r) => r.prompt_type === "category");
  const competitorsAgg = q.data?.competitors || [];

  // Group result rows by prompt_text → 4-engine matrix
  const byPrompt = new Map();
  for (const r of results) {
    if (!byPrompt.has(r.prompt_text)) {
      byPrompt.set(r.prompt_text, {
        prompt: { text: r.prompt_text, prompt_type: "category" },
        rows: [],
      });
    }
    byPrompt.get(r.prompt_text).rows.push(r);
  }
  const blocks = [...byPrompt.values()];

  if (!blocks.length) {
    return (
      <div className="rounded-md border border-slate-200 p-6 text-sm text-slate-500" data-testid="geo-menciones-empty">
        No hay prompts de categoría en esta run. Cargá prompts en la pestaña <strong>Prompts y configuración</strong> y volvé a ejecutar.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="geo-menciones-panel">
      {/* Per-prompt response blocks — FULL captions + citations + competitors */}
      <section>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">
          Respuestas completas por prompt
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Cada prompt muestra qué respondió cada motor de IA, palabra por palabra, con las fuentes que citó y los competidores que nombró. Click para expandir.
        </p>
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <PromptResponsesBlock
              key={i}
              prompt={b.prompt}
              rows={b.rows}
              defaultOpen={blocks.length === 1}
            />
          ))}
        </div>
      </section>

      {/* Aggregated competitors ranking — useful summary across all prompts */}
      {competitorsAgg.length > 0 && (
        <section>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-1">
            Ranking global de competidores nombrados
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Cuántas veces apareció cada competidor a través de todos los prompts y motores.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {competitorsAgg.slice(0, 16).map((c) => (
              <div
                key={c.name}
                className="rounded-md bg-white border border-slate-200 p-3 flex items-center justify-between"
                data-testid={`geo-competitor-${c.name}`}
              >
                <span className="text-sm font-medium text-slate-800 truncate" title={c.name}>
                  {c.name}
                </span>
                <span className="text-xs text-slate-500 shrink-0 ml-2">
                  {c.mentions} mención{c.mentions !== 1 ? "es" : ""}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
